import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import { Button, Container } from "@/components/atoms";
import { ConfirmationSuccessHeader } from "@/components/pages/Client/booking/confirmation/ConfirmationSuccessHeader";
import { MobileAppPromo } from "@/components/pages/Client/booking/confirmation/MobileAppPromo";
import { PriceSummary, PriceSummaryRow } from "@/components/pages/Client/booking/confirmation/PriceSummary";
import { TripDetails, TripDetailsCard } from "@/components/pages/Client/booking/confirmation/TripDetailsCard";
import { capacityLabel, formatDuration } from "@/components/pages/Client/booking/vehicleTransfer";
import { clearStashedGuestDraft, stashGuestDraft } from "@/components/pages/Guest/guestDraftStash";
import { isAirportPickup } from "@/helpers/bookingHelpers";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { transferRateKind } from "@/redux/slices/clientBooking/bookingPayload";
import { initialState } from "@/redux/slices/clientBooking/clientBookingSlice";
import { buildRebookDraft } from "@/redux/slices/clientBooking/rebookDraft";
import { ClientBookingVehicle, GuestContact } from "@/redux/slices/clientBooking/types";

/**
 * Serialisable snapshot handed to the confirmation via router state (GuestPricedBookingSummary.submit),
 * captured just before the draft is cleared. Everything the confirmation renders — no store reads, so a
 * hard refresh (which drops router state) falls back to the header + promo only.
 */
export interface GuestBookingConfirmationState {
  /** The created booking's display number ("J123456") — names the success heading. */
  displayJobId?: string;
  contact: GuestContact;
  /** Ordered `[pickup, ...stops, destination]` — first/last drive the route box. */
  locations: PointsOfInterest[];
  /** The arrival flight, shown under the pickup (DSN-689) — captured with the draft for the same reason as the rest. */
  pickupFlightNumber?: string | null;
  pickupNotFlying?: boolean;
  /** The departure flight, shown under the drop-off (DSN-689). */
  departureFlightNumber?: string | null;
  pickupDatetime?: string | null;
  vehicle?: ClientBookingVehicle | null;
  numOfPassengers?: number;
  /** Selected transfer type's label (e.g. "Airport Transfer") — drives the vehicle box's top line. */
  transferTypeLabel?: string;
  /** Selected transfer type (id + label) — carried so a re-book can restore it (`transferType.label` also feeds the display). */
  transferType?: { id: number; label?: string };
  /** Declared bag count — drives the vehicle box's "{n} Pax & {n} Bag(s)" subtitle. */
  numOfBags?: number;
  /** Notes to the driver (`additional_info`) — carried so a re-book can restore them. */
  additionalInfo?: string;
  /** Job rate type ("journey_rate" | "hourly_rate" | "daily_rate") — carried for a re-book. */
  jobRate?: string;
  /** Duration for hourly/daily rates — carried for a re-book. */
  bookedForDuration?: number;
  /** Price rows from the shared QuotePriceSummary mapping — matches the summary step exactly. */
  priceGroups?: PriceSummaryRow[][];
  total?: string;
  /** Captured card display metadata (Stripe `createPaymentMethod`) — drives the Payment box; omitted hides it. */
  card?: { label: string; expiry: string };
}

/** Map the router-state snapshot to the confirmation card's props. `card` is present only when the
 *  guest's captured Stripe card metadata made it into the snapshot (Payment box, Figma 5553-152145). */
const toTrip = (state: GuestBookingConfirmationState): TripDetails => {
  const {
    contact,
    locations,
    pickupFlightNumber,
    pickupNotFlying,
    departureFlightNumber,
    pickupDatetime,
    vehicle,
    numOfPassengers,
    transferType,
    transferTypeLabel,
    numOfBags,
    additionalInfo,
    card,
  } = state;

  const parsed = pickupDatetime ? new Date(pickupDatetime) : null;
  const isValidDate = !!parsed && !Number.isNaN(parsed.getTime());

  const pax = numOfPassengers ?? 1;
  const bags = numOfBags ?? 0;
  // Duration mirrors the Booking Summary for hourly/daily rates (guests never hit fixed-rate types —
  // those are admin-only — so there's no fixed rate to show here).
  const duration = transferRateKind(state.jobRate) !== "journey" ? formatDuration(state.bookedForDuration) || undefined : undefined;

  return {
    passenger: {
      name: `${contact.firstName} ${contact.lastName}`.trim(),
      email: contact.email,
      phone: contact.phone,
    },
    locations,
    pickupFlightNumber,
    pickupNotFlying,
    departureFlightNumber,
    date: isValidDate && parsed ? format(parsed, "d MMMM yyyy") : pickupDatetime || "—",
    time: isValidDate && parsed ? format(parsed, "h:mm a") : undefined,
    // Top line = transfer type (e.g. "Airport Transfer"); subtitle = "{vehicle} ({capacity})" (Figma 5553-152123).
    vehicleLabel: transferType?.label ?? transferTypeLabel ?? "—",
    vehicleType: vehicle?.name ? `${vehicle.name} (${capacityLabel(pax, bags)})` : undefined,
    duration,
    additionalInfo,
    card,
  };
};

/**
 * Guest booking confirmation. Shown after a successful guest booking-create. The account was
 * minted server-side and a welcome email sent (password setup). Reached at `/guest/booking/confirmation`.
 *
 * The summary step passes a serialisable snapshot via router state (captured before the draft is cleared),
 * so the confirmation renders the real trip + price — mirroring the authed ClientBookingConfirmation layout
 * (inline re-book actions + "Back to Home" on lg+, a fixed bottom action bar below lg), including the Payment
 * box when the snapshot carries captured card metadata. A guest has no session and re-booking needs a fresh
 * quote_uuid, so every action here deep-links to Kinde hosted login instead of routing within the app.
 * "Book a Return Trip" first stashes a re-book draft (pickup/dropoff reversed, same vehicle/transfer/pax/notes,
 * cleared date/flight/extras/payment — mirrors mobile SuccessScreen.onReturnBookingPress) so the authed resume
 * path prefills the booking after login; "Book an Interstate Trip" clears any stash and opens a fresh booking.
 * Interstate only shows for a flight/airport trip (mirrors mobile's SuccessScreen); "Book a Return Trip" and
 * "Back to Home" always show. On a hard refresh / direct nav the state is absent and it degrades to the
 * success header + "download the app" promo only.
 */
export const GuestBookingConfirmation = () => {
  const { state } = useLocation();
  const snapshot = (state as GuestBookingConfirmationState | null) ?? null;
  const { login } = useKindeAuth();

  // Mirrors mobile's SuccessScreen: "Book an Interstate Trip" only makes sense for a flight/airport
  // trip (pickup or dropoff), so only show it then — unlike "Book a Return Trip"/"Back to Home", which
  // always render.
  const isFlightTrip =
    !!snapshot && (isAirportPickup(snapshot.locations[0]) || isAirportPickup(snapshot.locations[snapshot.locations.length - 1]));

  // A guest has no session and booking again requires a fresh quote_uuid, so every re-book action deep-links
  // to Kinde hosted login rather than routing within the app. "Back to Home" carries no draft.
  const goToLogin = () => login({});

  // "Book a Return Trip" (mirrors mobile SuccessScreen.onReturnBookingPress): seed the next booking with
  // pickup/dropoff reversed, the same vehicle/transfer/passengers/bags/notes/rate, and a cleared date/flight/
  // extras/payment. Stash the full draft so the authed resume path (ClientPortal → ClientBooking →
  // restoreBookingDraft) prefills the booking after login. Falls back to a plain login if the snapshot is absent.
  const bookReturnTrip = () => {
    if (!snapshot) return login({});
    const draft = buildRebookDraft(
      {
        locations: snapshot.locations,
        vehicle: snapshot.vehicle,
        transferType: snapshot.transferType,
        numOfPassengers: snapshot.numOfPassengers,
        numOfBags: snapshot.numOfBags,
        additionalInfo: snapshot.additionalInfo,
        jobRate: snapshot.jobRate,
        bookedForDuration: snapshot.bookedForDuration,
      },
      "return"
    );
    // stashGuestDraft persists a full ClientBookingState; merge the partial over initialState so any field
    // not set by the re-book resets cleanly (restoreBookingDraft merges onto initialState too).
    stashGuestDraft({ ...initialState, ...draft });
    login({});
  };

  // "Book an Interstate Trip": mobile opens a fresh booking. Clear any stale stash first so a leftover
  // return draft can't prefill the blank booking, then log in.
  const bookInterstateTrip = () => {
    clearStashedGuestDraft();
    login({});
  };

  return (
    <div className="flex flex-1 flex-col bg-white lg:bg-transparent">
      {/* Confirmation + trip details (card chrome only on lg+) */}
      <div className="lg:bg-neutral-surface-gray">
        <Container className="flex flex-col items-center px-5 pt-[60px] md:px-[60px] lg:px-4 lg:pb-[18px] lg:pt-14">
          <ConfirmationSuccessHeader displayJobId={snapshot?.displayJobId} />

          {snapshot && (
            <>
              <div className="mt-6 w-full max-w-[1000px] lg:rounded-2xl lg:border lg:border-neutral-mid-gray lg:bg-white lg:p-[30px] lg:shadow-sm">
                <div className="flex flex-col gap-6 lg:flex-row lg:gap-10">
                  <div className="flex-1">
                    <TripDetailsCard trip={toTrip(snapshot)} />
                  </div>
                  {/* Only when the snapshot actually carries a price — never fall back to PriceSummary's
                      layout-only sample, which would show a real guest a fabricated total. */}
                  {snapshot.priceGroups && snapshot.total && (
                    <div className="lg:w-[320px] lg:shrink-0">
                      <PriceSummary groups={snapshot.priceGroups} total={snapshot.total} />
                    </div>
                  )}
                </div>

                {/* Inline actions — desktop only (tablet/mobile use the fixed bottom bar) */}
                <div className="mt-8 hidden justify-center gap-3 lg:flex">
                  {isFlightTrip && (
                    <Button variant="secondary" size="md" onClick={bookInterstateTrip}>
                      Book an Interstate Trip
                    </Button>
                  )}
                  <Button variant="primary" size="md" onClick={bookReturnTrip}>
                    Book a Return Trip
                  </Button>
                </div>
              </div>

              {/* Back to Home — desktop only */}
              <Button variant="custom" size="lg" onClick={goToLogin} className="mt-5 hidden text-info lg:inline-flex">
                Back to Home
              </Button>
            </>
          )}
        </Container>
      </div>

      {/* Mobile app promo band — full width */}
      <div className="bg-white">
        <Container className="px-5 pb-8 pt-14 md:px-[60px] lg:px-4 lg:pb-14 lg:pt-[65px]">
          <MobileAppPromo />
        </Container>
      </div>

      {/* Fixed bottom action bar — tablet/mobile only */}
      {snapshot && (
        <div className="sticky bottom-0 z-10 border-t border-neutral-mid-gray bg-white p-4 lg:hidden">
          <div className="flex flex-col gap-2">
            {/* Tablet: side-by-side (Interstate left, Return right). Mobile: stacked (Return on top). */}
            <div className="flex flex-col gap-2 md:flex-row-reverse md:items-center md:justify-center md:gap-3">
              <Button variant="primary" size="lg" className="w-full md:w-auto" onClick={bookReturnTrip}>
                Book a Return Trip
              </Button>
              {isFlightTrip && (
                <Button variant="secondary" size="lg" className="w-full md:w-auto" onClick={bookInterstateTrip}>
                  Book an Interstate Trip
                </Button>
              )}
            </div>
            <Button variant="custom" size="md" onClick={goToLogin} className="text-info">
              Back to Home
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
